import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import AppLayout from './components/AppLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import TransactionsPage from './pages/TransactionsPage'
import SalesPage from './pages/SalesPage'
import ReportsPage from './pages/ReportsPage'
import IntegrationsPage from './pages/IntegrationsPage'
import ReconciliationPage from './pages/ReconciliationPage'
import FilesPage from './pages/FilesPage'
import InvoicePage from './pages/InvoicePage'
import OverdueInvoicesPage from './pages/OverdueInvoicesPage'
import InvoiceSettingsPage from './pages/InvoiceSettingsPage'
import AuditTrailPage from './pages/AuditTrailPage'
import VatReturnsPage from './pages/VatReturnsPage'
import TaxReportsPage from './pages/TaxReportsPage'
import MissingReceiptsPage from './pages/MissingReceiptsPage'
import BankStatementsPage from './pages/BankStatementsPage'
// BankSyncSettingsPage removed — functionality moved to IntegrationsPage
import AIReconciliationPage from './pages/AIReconciliationPage'
import ReceiptMatchingPage from './pages/ReceiptMatchingPage'
import OpeningEntriesPage from './pages/OpeningEntriesPage'
import SkillsPage from './pages/SkillsPage'
import JournalPage from './pages/JournalPage'
import GeneralLedgerPage from './pages/GeneralLedgerPage'
import MembersPage from './pages/MembersPage'
import MemberDuesPage from './pages/MemberDuesPage'
import BudgetAlertsPage from './pages/BudgetAlertsPage'
import CategoryBudgetLimitsPage from './pages/CategoryBudgetLimitsPage'
import OnboardingPage from './pages/OnboardingPage'
import SetupWizardPage from './pages/SetupWizardPage'
import PrivacySettingsPage from './pages/PrivacySettingsPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import OrganizationSettingsPage from './pages/OrganizationSettingsPage'
import NotificationSettingsPage from './pages/NotificationSettingsPage'

// Global error handlers
if (typeof window !== 'undefined') {
  // Capture unhandled JS errors
  window.addEventListener('error', (event) => {
    import('./api/errorReporting').then(({ reportFrontendError }) => {
      reportFrontendError(
        event.error || event.message || 'Unknown error',
        'window_onerror',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      );
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    import('./api/errorReporting').then(({ reportFrontendError }) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      reportFrontendError(error, 'unhandled_promise_rejection');
    });
  });
}

function App() {
  // Cookie-based session is automatic, no manual restoration needed
  return (
    <ErrorBoundary>
      <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* Onboarding route - outside AppLayout so it's full-screen */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/setup" element={<SetupWizardPage />} />
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/sales" element={<SalesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/settings/integrations" element={<IntegrationsPage />} />
            <Route path="/reconciliation" element={<ReconciliationPage />} />
            <Route path="/invoices" element={<InvoicePage />} />
            <Route path="/invoices/overdue" element={<OverdueInvoicesPage />} />
            <Route path="/settings/invoices" element={<InvoiceSettingsPage />} />
            <Route path="/audit-trail" element={<AuditTrailPage />} />
            <Route path="/vat-returns" element={<VatReturnsPage />} />
            <Route path="/tax-reports" element={<TaxReportsPage />} />
            <Route path="/missing-receipts" element={<MissingReceiptsPage />} />
            <Route path="/bank-statements" element={<BankStatementsPage />} />
            <Route path="/reconciliation/ai" element={<AIReconciliationPage />} />
            <Route path="/receipt-matching" element={<ReceiptMatchingPage />} />
            <Route path="/accounting/opening-entries" element={<OpeningEntriesPage />} />
            <Route path="/settings/skills" element={<SkillsPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/general-ledger" element={<GeneralLedgerPage />} />
            <Route path="/settings/bank-sync" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/members/dues" element={<MemberDuesPage />} />
            <Route path="/budget-alerts" element={<BudgetAlertsPage />} />
            <Route path="/category-budget-limits" element={<CategoryBudgetLimitsPage />} />
            <Route path="/settings/privacy" element={<PrivacySettingsPage />} />
<Route path="/settings/organization" element={<OrganizationSettingsPage />} />
            <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            {/* Legacy redirects for backward compatibility */}
            <Route path="/sumup" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/op" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/holvi" element={<Navigate to="/settings/integrations" replace />} />
            <Route path="/nordea" element={<Navigate to="/settings/integrations" replace />} />
          </Route>
        </Route>

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
